import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, LogOut, LayoutDashboard, ShieldCheck, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Seal from "@/components/Seal";

const links = [
  { to: "/about", label: "About" },
  { to: "/code-of-conduct", label: "Code of Conduct" },
  {
    label: "Process",
    children: [
      { to: "/certification-process", label: "Certification Process" },
      { to: "/questionnaire-document-checklist", label: "Questionnaire & Document Checklist" },
    ],
  },
  { to: "/assessment-criteria", label: "Criteria" },
  { to: "/pricing", label: "Pricing" },
  { to: "/verify", label: "Verify" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header data-testid="public-navbar" className="sticky top-0 z-50 border-b border-[var(--wechr-border)] bg-white/95 backdrop-blur">
      <nav className="wechr-container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <Seal size={30} />
          <div className="leading-none">
            <span className="font-heading text-lg font-bold text-navy-900">WECH-RB</span>
            <span className="ml-1 hidden text-xs text-[var(--wechr-muted-text)] sm:inline">DD Certification</span>
          </div>
        </Link>

        <div className="hidden items-center gap-6 lg:flex">
          {links.map((l) =>
            l.children ? (
              <DropdownMenu key={l.label}>
                <DropdownMenuTrigger
                  className={`flex items-center gap-1 text-sm font-medium outline-none transition-colors hover:text-wgreen-700 ${l.children.some((c) => c.to === location.pathname) ? "text-wgreen-700" : "text-navy-800"}`}
                  data-testid="nav-process-trigger"
                >
                  {l.label} <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  {l.children.map((c) => (
                    <DropdownMenuItem key={c.to} asChild>
                      <Link to={c.to} className="cursor-pointer" data-testid={`nav-process-item-${c.to}`}>{c.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium transition-colors hover:text-wgreen-700 ${location.pathname === l.to ? "text-wgreen-700" : "text-navy-800"}`}
              >
                {l.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              {isAdmin ? (
                <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="nav-admin-link">
                  <ShieldCheck className="mr-1 h-4 w-4" /> Admin
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-link">
                  <LayoutDashboard className="mr-1 h-4 w-4" /> Dashboard
                </Button>
              )}
              <Button variant="outline" onClick={handleLogout} data-testid="nav-logout-button">
                <LogOut className="mr-1 h-4 w-4" /> Log out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-navy-800 hover:text-wgreen-700" data-testid="public-navbar-login-link">Login</Link>
              <Button className="btn-green" onClick={() => navigate("/apply")} data-testid="public-navbar-apply-button">Apply now</Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="nav-mobile-toggle"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-6 flex flex-col gap-1">
                {links.map((l) =>
                  l.children ? (
                    <div key={l.label} className="mt-1">
                      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--wechr-muted-text)]">{l.label}</p>
                      {l.children.map((c) => (
                        <SheetClose asChild key={c.to}>
                          <Link to={c.to} className="block rounded-md px-5 py-2 text-navy-800 hover:bg-[var(--wechr-subtle)]">{c.label}</Link>
                        </SheetClose>
                      ))}
                    </div>
                  ) : (
                    <SheetClose asChild key={l.to}>
                      <Link to={l.to} className="rounded-md px-3 py-2 text-navy-800 hover:bg-[var(--wechr-subtle)]">{l.label}</Link>
                    </SheetClose>
                  )
                )}
                <div className="mt-4 flex flex-col gap-2 border-t pt-4">
                  {user ? (
                    <>
                      <SheetClose asChild>
                        <Button variant="outline" onClick={() => navigate(isAdmin ? "/admin" : "/dashboard")}>{isAdmin ? "Admin" : "Dashboard"}</Button>
                      </SheetClose>
                      <Button variant="outline" onClick={handleLogout}>Log out</Button>
                    </>
                  ) : (
                    <>
                      <SheetClose asChild>
                        <Button variant="outline" onClick={() => navigate("/login")}>Login</Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button className="btn-green" onClick={() => navigate("/apply")}>Apply now</Button>
                      </SheetClose>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
